/**
 * GDPR ẩn danh nhất quán (người-rời) + bằng-chứng-là-CLAIM (US-E19.F5-0204 · @UC9312 @UC9313 @UC9314).
 * 🔴 @gate pii + money.
 *
 * THUẦN: không DB, không I/O, KHÔNG `Date.now()`, KHÔNG `Math.random()` — cùng input ⇒ cùng output.
 *
 * 🔴 HAI BẤT BIẾN CỦA TASK, ĐẶT Ở ĐÂY (1 NGUỒN SỰ THẬT):
 *
 * (1) GDPR = **ẨN DANH, KHÔNG XOÁ SỔ** (P7.3). Sau khi ẩn danh, mọi bút toán/nợ mà người đó có phần
 *     VẪN CÒN NGUYÊN (append-only), chỉ danh tính bị cắt. Chỗ nào render tên người đã ẩn danh thì
 *     hiện `ANONYMIZED_MEMBER_DISPLAY_NAME` — KHÔNG rơi về email/uuid/null (null ⇒ FE dễ crash hoặc
 *     rơi về fallback lộ email cũ). Đây là nhãn hiển thị DUY NHẤT cho người đã rời + đã ẩn danh.
 *
 * (2) EVIDENCE = **CLAIM, KHÔNG PHẢI FACT** (P7.3 / SEC-FAKE-PROOF). Bằng chứng người dùng nộp (ảnh
 *     bill, ảnh xác nhận CK) mặc định `unverified`. Chuyển sang `valid`/`invalid` là hành động CÓ CHỦ
 *     THỂ (owner/treasurer soát — có `verifiedBy` + `verifiedAt`). REUSE nguyên xi bộ enum
 *     `VERIFICATION_STATUS` của E19.F4 (`compliance-tax-kyc.ts`) — **KHÔNG dựng bộ từ vựng thứ hai**
 *     ("claim" chỉ là CÁCH GỌI của `unverified`; thêm giá trị 'claim' vào DB = 2 whitelist lệch nhau).
 */

import { VERIFICATION_STATUS, type VerificationStatus } from "./compliance-tax-kyc.js";
import { GROUP_TYPE_PARENT, GROUP_TYPE_STANDARD, resolveSeatDisplayName } from "./obligation-scope.js";

/* ─────────────────────── (1) GDPR — nhãn hiển thị người đã ẩn danh ─────────────────────── */

/**
 * 🔴 Nhãn hiển thị của thành viên đã rời + đã ẩn danh (AC2). Bút toán/nợ của họ VẪN hiện trong lịch
 * sử (không được xoá — append-only), nhưng danh tính thì KHÔNG: không email cũ, không tên thật,
 * không SĐT. Hằng số này là NHÃN DUY NHẤT — mọi nơi render tên phải dùng nó (không tự chế chuỗi khác).
 */
export const ANONYMIZED_MEMBER_DISPLAY_NAME = "Thành viên đã rời" as const;

/**
 * Nhãn chung khi một suất/người KHÔNG có tên nào để hiện (không biệt danh, không họ tên, không được dùng email). KHÔNG phải tên
 * người — nơi dựng câu có tên người (vd "bạn nhắn …") phải BỎ nhãn này, không chèn nó như một cái tên. Một hằng, không literal rải rác.
 */
export const DEFAULT_MEMBER_DISPLAY_NAME = "Thành viên" as const;

/** Trạng thái `users.status` sau khi ẩn danh (khớp `delete-me.service.ts` — 1 nguồn sự thật). */
export const ANONYMIZED_USER_STATUS = "anonymized" as const;

/**
 * Nguồn tối thiểu để quyết định nhãn hiển thị của 1 membership. `userStatus` = `users.status`;
 * `displayName` = `group_members.displayName` (nickname per-group — CŨNG LÀ PII, phải null sau ẩn danh).
 *
 * 🔴 US-AIV2-0111 C09: `fullName` (= `users.fullName` của người giữ suất) và `groupType` (= `groups.type`) là
 * trường BẮT BUỘC (kiểu `| null`, không `?`): thiếu họ tên chính là lỗi C09 — "ai nợ ai" hiện `mai`/`Thành viên`
 * cho người Zalo. Bắt buộc ở kiểu ⇒ nơi gọi mới không thể quên select cột đó mà typecheck vẫn im.
 */
export interface MemberDisplaySource {
  readonly userStatus: string | null;
  readonly displayName: string | null;
  readonly email: string | null;
  readonly fullName: string | null;
  readonly groupType: string | null;
  /**
   * 🔴 review #1025 MEDIUM-2 — suất này có phải SUẤT TỰ THÂN của người giữ cache `primaryUser` không (`seatHolderSelf`).
   * Chỉ quyết ở nhóm PHỤ HUYNH, nơi `primaryUserId` của suất BÉ là phụ huynh: phụ huynh xoá tài khoản thì suất bé KHÔNG phải
   * "người đã rời" — bé vẫn trong lớp, vẫn còn nợ. Vắng/`null` = không biết ⇒ fail-closed (áp vế ẩn danh như trước).
   */
  readonly holderSelfSeat?: boolean | null;
}

/**
 * Nguồn của luật tên KHÔNG dùng email — cho bề mặt không được lộ tiền tố email (trang công khai `/t/:token`, câu trợ lý tới LLM,
 * hợp đồng `name: string | null` cũ). Cùng các trường bắt buộc của `MemberDisplaySource` trừ `email`.
 */
export type MemberNameSource = Omit<MemberDisplaySource, "email">;

/** Chuỗi đã cắt khoảng trắng, rỗng ⇒ `null` (để `??` của luật tên suất không dừng ở chuỗi rỗng). */
function nonBlank(value: string | null): string | null {
  const t = value?.trim();
  return t ? t : null;
}

/**
 * Nhãn hiển thị của 1 membership (AC2 — thuần, deterministic).
 *
 * 🔴 FAIL-CLOSED VỀ PII: user đã `anonymized` ⇒ LUÔN `ANONYMIZED_MEMBER_DISPLAY_NAME`, **KỂ CẢ KHI**
 * `displayName`/`fullName`/`email` chưa được null-hoá (dữ liệu cũ / purge nửa vời). Nghĩa là: kể cả nếu ai đó
 * quên xoá 1 cột PII, nhãn hiển thị VẪN không rò tên/email cũ. Đảo thứ tự (đọc tên trước status) ⇒ PII rò ⇒
 * test ĐỎ.
 *
 * Người CÒN hoạt động (US-AIV2-0111 C09): biệt danh trong nhóm → họ tên → tiền tố email → "Thành viên".
 * Hai vế đầu là luật tên suất DUY NHẤT `resolveSeatDisplayName` (không chép lại): ở nhóm PHỤ HUYNH suất là
 * đứa trẻ, chưa đặt tên thì KHÔNG mượn họ tên phụ huynh. Tiền tố email giữ nguyên như trước C09 (0 regress);
 * email đầy đủ không bao giờ ra khỏi hàm này.
 *
 * 🔴 review #1011 M1: vế tiền tố email CŨNG KHÔNG áp cho nhóm phụ huynh — email là của NGƯỜI ĐẠI DIỆN, không phải
 * của đứa trẻ, nên mượn tiền tố của nó (`hoa.pham`) là đúng lỗi "mượn danh tính phụ huynh" mà vế họ tên vừa chặn,
 * chỉ qua một cột khác. Suất con chưa đặt tên ⇒ "Thành viên".
 */
export function resolveMemberDisplayName(source: MemberDisplaySource): string {
  const name = resolveMemberNameOrNull(source);
  if (name !== null) return name;
  if (source.groupType === GROUP_TYPE_PARENT) return DEFAULT_MEMBER_DISPLAY_NAME;
  const email = source.email ?? "";
  const at = email.indexOf("@");
  if (at > 0) return email.slice(0, at);
  return DEFAULT_MEMBER_DISPLAY_NAME;
}

/**
 * 🔴 US-AIV2-0111 PII-NAMES (@UC9312) — CÙNG luật với `resolveMemberDisplayName`, dạng nullable và KHÔNG email: ẩn danh ⇒ nhãn
 * "đã rời" (fail-closed, đứng TRƯỚC mọi cột tên) → luật tên suất `resolveSeatDisplayName` (phụ huynh không mượn tên người đại
 * diện) → `null` khi không có tên. `resolveMemberDisplayName` được dựng TRÊN hàm này, nên hai bản không lệch được.
 *
 * Vì sao cần: các bề mặt tự viết `displayName ?? primaryUser.fullName` bỏ qua vế ẩn danh ⇒ biệt danh do quản trị đặt còn hiện
 * mãi sau `DELETE /v1/me` (lỗ thật ở trang công khai `/t/:token`). Nơi cần chuỗi thì `?? DEFAULT_MEMBER_DISPLAY_NAME`.
 */
export function resolveMemberNameOrNull(source: MemberNameSource): string | null {
  if (source.userStatus === ANONYMIZED_USER_STATUS && !isChildSeatOfAnonymizedParent(source)) {
    return ANONYMIZED_MEMBER_DISPLAY_NAME;
  }
  return resolveSeatDisplayName(source.groupType, nonBlank(source.displayName), nonBlank(source.fullName));
}

/**
 * 🔴 review #1025 MEDIUM-2 (@UC9312 @UC0024) — ngoại lệ DUY NHẤT của vế ẩn danh: suất KHÔNG tự thân ở nhóm phụ huynh.
 *
 * `userStatus` là trạng thái của NGƯỜI GIỮ CACHE `primaryUserId`. Ở nhóm phụ huynh người đó là phụ huynh, còn suất là ĐỨA TRẺ: bố xoá
 * tài khoản không làm "Bé Na" rời lớp — suất vẫn sống, vẫn còn nợ đợt sau. Áp nhãn "Thành viên đã rời" ở đó là lời khai SAI trên màn
 * tiền (bảng thu, trang công khai, link suất, PDF): thủ quỹ không biết bé nào. Tên bé không phải PII của phụ huynh; họ tên/email của
 * phụ huynh vẫn KHÔNG được mượn (`resolveSeatDisplayName` nhóm phụ huynh chỉ đọc biệt danh của suất).
 *
 * CHỈ `=== false` (đã nạp, biết chắc không tự thân): vắng/`null` ⇒ áp vế ẩn danh (fail-closed về PII — biệt danh tự thân còn sót của
 * người lớn không lộ vì một nơi gọi quên nạp liên kết). Nhóm thường: suất là của chính người đó ⇒ cờ không nới gì.
 */
function isChildSeatOfAnonymizedParent(source: MemberNameSource): boolean {
  return source.groupType === GROUP_TYPE_PARENT && source.holderSelfSeat === false;
}

/**
 * 🔴 review #1025 MEDIUM-2 — suất có phải SUẤT TỰ THÂN của người giữ cache không: có liên kết `linkRole = self` của CHÍNH
 * `primaryUserId` (nơi gọi nạp `coLogins: { where: { linkRole: "self" }, select: { userId: true } }` — không lọc `deletedAt`: một liên
 * kết tự thân dù đã gỡ vẫn nói suất đó từng là của chính người ấy). `coLogins` vắng (không nạp) ⇒ `null` = không biết.
 */
export function seatHolderSelf(seat: {
  readonly primaryUserId: string | null;
  readonly coLogins?: readonly { readonly userId: string }[];
}): boolean | null {
  if (seat.coLogins === undefined) return null;
  const holder = seat.primaryUserId;
  return holder !== null && seat.coLogins.some((l) => l.userId === holder);
}

/**
 * 🔴 US-AIV2-PII-2 (review #1025 MEDIUM-1, @UC9312) — HÀNG `group_members` đủ cột cho luật tên. Select Prisma nằm ở mỗi app (domain không
 * dính Prisma — api `MEMBER_NAME_SELECT`, worker select tại chỗ), còn phép CHIẾU hàng ⇒ luật nằm ở đây, MỘT nguồn cho api + worker.
 * `status` của người giữ suất BẮT BUỘC (thiếu ⇒ typecheck đỏ): không có nó vế ẩn danh không chạy được — đúng lỗ của bốn chỗ đọc chỉ `displayName`.
 * `fullName` tuỳ chọn: bề mặt chỉ in biệt danh (`memberNicknameOfRow`) không cần đọc họ tên.
 */
export interface MemberNameRow {
  readonly displayName: string | null;
  readonly primaryUser: { readonly fullName?: string | null; readonly status: string } | null;
  readonly group: { readonly type: string };
  readonly primaryUserId: string | null;
  /**
   * Liên kết TỰ THÂN của suất (`SEAT_SELF_LINK_ROW_SELECT`). VẮNG = không nạp ⇒ `seatHolderSelf` = `null` ⇒ vế ẩn danh áp (fail-closed về PII:
   * suất bé của phụ huynh đã xoá TK hiện "đã rời" — sai về tên nhưng không lộ). Nạp thì luật nới đúng ngoại lệ phụ huynh (#1025 MEDIUM-2).
   */
  readonly coLogins?: readonly { readonly userId: string }[];
}

/**
 * 🔴 PII-2 — liên kết TỰ THÂN của suất (cho `seatHolderSelf`): mọi liên kết `linkRole = self`, KHÔNG lọc `deletedAt` (liên kết đã gỡ vẫn nói suất
 * từng là của chính người ấy). Object thuần (không kiểu Prisma — domain không dính ORM), cùng khuôn `SEAT_RECIPIENT_SELECT`: app spread vào
 * `select`. `"self"` = `MEMBERSHIP_LINK_ROLE.SELF` của contracts (api spec khoá hai chuỗi bằng nhau).
 */
export const SEAT_SELF_LINK_ROW_SELECT = {
  primaryUserId: true,
  coLogins: { where: { linkRole: "self" }, select: { userId: true } },
} as const;

/**
 * 🔴 PII-2 — `select` đủ cột cho `MemberNameRow` (api `MEMBER_NAME_SELECT` và worker spread CHÍNH hằng này — một hình, không chép).
 * Nơi đã nạp `coLogins` theo luật khác (người nhận thông báo) không spread được hằng này (trùng khoá) ⇒ nạp liên kết tự thân riêng.
 */
export const MEMBER_NAME_ROW_SELECT = {
  displayName: true,
  primaryUser: { select: { fullName: true, status: true } },
  group: { select: { type: true } },
  ...SEAT_SELF_LINK_ROW_SELECT,
} as const;

/**
 * 🔴 review #1067 LOW-2 — `select` cho bề mặt CHỈ biệt danh (`memberNicknameOfRow`): như `MEMBER_NAME_ROW_SELECT` nhưng KHÔNG đọc `fullName`
 * (không đọc lên thì không lộ được — cột họ tên không có việc gì ở tin trả nợ / CSV / tìm kiếm).
 */
export const MEMBER_NICKNAME_ROW_SELECT = {
  displayName: true,
  primaryUser: { select: { status: true } },
  group: { select: { type: true } },
  ...SEAT_SELF_LINK_ROW_SELECT,
} as const;

/**
 * 🔴 review #1067 HIGH-1 · LOW-1 — phép chiếu SANG DB của vế "đã rời" của luật tên, cho TÌM THEO CHỮ: loại suất mà luật sẽ in "Thành viên đã
 * rời" NGAY trong `where` (lọc sau `take` làm trang ngắn + `nextCursor` lộ "có người khớp"). Object thuần (domain không dính ORM); caller
 * dùng `{ NOT: seatNameHiddenWhere() }`. Hàm (không hằng) ⇒ mỗi lần một object mới, không mảng `readonly` đi vào `OR` của Prisma.
 *
 * Quan hệ với luật (`resolveMemberNameOrNull`) — CHỈ một chiều, có chủ đích: mọi suất luật che ⇒ `where` loại (người giữ đã ẩn danh VÀ (nhóm
 * thường HOẶC suất có liên kết `self`)). Chiều ngược KHÔNG khít: nhóm phụ huynh, suất có liên kết `self` của NGƯỜI KHÁC người giữ cache —
 * luật in tên bé, `where` loại khỏi kết quả TÌM (không khỏi danh sách) ⇒ tìm ẩn bớt, không bao giờ tìm ra thứ luật che.
 */
export function seatNameHiddenWhere(): {
  primaryUser: { is: { status: string } };
  OR: [{ group: { is: { type: { not: string } } } }, { coLogins: { some: { linkRole: string } } }];
} {
  return {
    primaryUser: { is: { status: ANONYMIZED_USER_STATUS } },
    OR: [{ group: { is: { type: { not: GROUP_TYPE_PARENT } } } }, { coLogins: { some: { linkRole: SEAT_SELF_LINK_ROW_SELECT.coLogins.where.linkRole } } }],
  };
}

/** Tên của suất theo luật DUY NHẤT (ẩn danh ⇒ "Thành viên đã rời"; phụ huynh ⇒ tên bé, không mượn tên người lớn), hoặc `null`. */
export function memberNameOfRow(row: MemberNameRow): string | null {
  return resolveMemberNameOrNull({
    userStatus: row.primaryUser?.status ?? null,
    displayName: row.displayName,
    fullName: row.primaryUser?.fullName ?? null,
    groupType: row.group.type,
    holderSelfSeat: seatHolderSelf(row),
  });
}

/**
 * 🔴 PII-2 — CÙNG luật, KHÔNG nới sang họ tên (`fullName: null`): cho bề mặt trước nay CHỈ in biệt danh (tìm thành viên, thông báo "người
 * được trả", tên suất trong tin nhắc, cột `display_name` của CSV xuất nhóm — file lưu R2). PII-2 đóng vế ẩn danh ở các chỗ đó; mở thêm họ
 * tên vào một thông báo/tệp là QĐ riêng của từng bề mặt, không đi lậu trong bản vá PII. Vế ẩn danh + ngoại lệ phụ huynh áp y hệt.
 */
export function memberNicknameOfRow(row: MemberNameRow): string | null {
  return memberNameOfRow({ ...row, primaryUser: row.primaryUser === null ? null : { status: row.primaryUser.status, fullName: null } });
}

/**
 * 🔴 review #1145 LOW [PII] (@UC9312) — HỌ TÊN người giữ suất được phép rời server trên một hàng danh sách thành viên (`GET /v1/groups/:id/members`):
 * người đã ẩn danh ⇒ `null`, KHÔNG phụ thuộc cột `users.full_name` đã bị gạt hay chưa (`delete-me`/`anonymize-left-member` gạt, nhưng dữ liệu cũ hay
 * một đường ẩn danh mai này quên gạt thì cột còn). Đặt ở chỗ GHI hàng ra vì đó là chỗ DUY NHẤT biết `users.status` — hàng của hợp đồng không mang
 * nó, nên `memberListRowName` (trợ lý) và màn Thành viên (mobile) đều chỉ thấy kết quả. Ca thật: ghế người lớn ở nhóm phụ huynh mà liên kết `self`
 * là của NGƯỜI KHÁC người giữ cache — `displayName` giữ rỗng (#1025 MEDIUM-2), `adultSeat` đi luật nhóm thường ⇒ rơi xuống họ tên này.
 */
export function memberListHolderFullName(holder: { readonly status: string; readonly fullName: string | null } | null | undefined): string | null {
  if (holder === null || holder === undefined || holder.status === ANONYMIZED_USER_STATUS) return null;
  return holder.fullName;
}

/**
 * 🔴 READ-PEOPLE-MINE-MEMBER-NAMES (QĐ PO 02/10 — dòng 🔶 bảng §8 `sdlc_opt/01-backlog/lanes-US-AIV2-0111-0118.md`; @UC9312 @UC0207 @UC0055) — một
 * HÀNG của danh sách thành viên (`GET /v1/groups/:id/members`, mục `data`/`managers`/`pendingMembers`) như server đã trả.
 *  · `displayName` ĐÃ qua luật ở server (`memberNicknameOfRow`): người đã ẩn danh ⇒ nhãn "đã rời" (≠ `null`), TRỪ suất không tự thân ở nhóm phụ
 *    huynh (#1025 MEDIUM-2 — suất bé của phụ huynh đã xoá TK giữ tên bé, có thể RỖNG). Ca đó gồm cả ghế mang `adultSeat` mà liên kết `self` là của
 *    NGƯỜI KHÁC người giữ cache (review #1145): `displayName` rỗng + `adultSeat` đi luật nhóm thường ⇒ sẽ rơi xuống `fullName`. Vì vậy vế ẩn danh
 *    KHÔNG nằm trọn trong `displayName` — nó nằm ở `fullName` (dòng dưới).
 *  · `fullName` = họ tên của người giữ suất như server cho rời (`memberListHolderFullName`: người đã ẩn danh ⇒ `null`, kể cả khi cột
 *    `users.full_name` chưa bị gạt). Ở nhóm phụ huynh: của PHỤ HUYNH, không phải của bé.
 *  · `adultSeat` = ghế NGƯỜI LỚN (`self`) ở nhóm phụ huynh — suất có liên kết `self` (H10).
 */
export interface MemberListRowNameSource {
  readonly displayName: string | null;
  readonly fullName: string | null;
  readonly adultSeat?: true | undefined;
}

/**
 * 🔴 READ-PEOPLE-MINE-MEMBER-NAMES — TÊN của một hàng danh sách thành viên theo ĐÚNG luật màn Thành viên: biệt danh, không có thì HỌ TÊN; suất BÉ
 * nhóm phụ huynh KHÔNG mượn họ tên phụ huynh; ghế NGƯỜI LỚN dùng họ tên của chính họ. Đi qua luật tên DUY NHẤT (`resolveMemberNameOrNull` →
 * `resolveSeatDisplayName`) — không chép `displayName ?? fullName` (chốt `member-name-rule.invariants.spec.ts`).
 *
 * `groupType` = `groups.type` (ở API: `category.type` của response — `buildGroupCategoryEmbedded` lấy đúng cột luật của nhóm). Ngoài đúng hai giá
 * trị của CHECK `groups_type_check` (`standard` | `parent`) — vắng, `null`, chuỗi rỗng, loại lạ — ⇒ FAIL-CLOSED về biệt danh NGAY TRONG HÀM (review
 * #1145: không dựa vào nơi gọi): không biết hàng là suất bé hay không thì không được mở họ tên (mở sai chiều = in họ tên phụ huynh làm tên bé).
 *
 * `userStatus: null` có chủ đích: hàng của cửa GUI không mang `users.status`. Vế ẩn danh đã chạy ở server, ở HAI cột: `displayName`
 * (`memberNicknameOfRow`) và `fullName` (`memberListHolderFullName` — người đã ẩn danh ⇒ `null`). Docblock `MemberListRowNameSource`.
 * Trả `null` khi không có tên nào — mỗi bề mặt giữ nhãn "chưa rõ tên" của nó.
 */
export function memberListRowName(row: MemberListRowNameSource, groupType: string | null | undefined): string | null {
  if (groupType !== GROUP_TYPE_STANDARD && groupType !== GROUP_TYPE_PARENT) return nonBlank(row.displayName);
  return resolveMemberNameOrNull({
    userStatus: null,
    displayName: row.displayName,
    fullName: row.fullName,
    // Ghế người lớn ở nhóm phụ huynh là suất TỰ THÂN ⇒ luật nhóm thường (biệt danh → họ tên của chính họ).
    groupType: row.adultSeat === true ? GROUP_TYPE_STANDARD : groupType,
  });
}

/* ─────────────────────── (2) Evidence = CLAIM (không tin mù) ─────────────────────── */

/**
 * 🔴 Trạng thái xác minh của bằng chứng MỚI nộp = `unverified` (= "claim"). REUSE
 * `DEFAULT_VERIFICATION_STATUS` của E19.F4 nhưng đặt tên theo ngữ cảnh evidence để đọc code rõ nghĩa.
 * Chỉ 1 giá trị, 1 whitelist DB — không có enum 'claim' thứ hai.
 */
export const EVIDENCE_CLAIM_STATUS: VerificationStatus = VERIFICATION_STATUS.UNVERIFIED;

/** Nhãn hiển thị cho FE khi bằng chứng chưa được ai soát (AC1 — "chưa xác minh"). */
export const EVIDENCE_UNVERIFIED_LABEL = "Chưa xác minh" as const;

/**
 * Trạng thái mà một hành động SOÁT được phép đặt: `valid` | `invalid`. **KHÔNG** cho phép quay về
 * `unverified` (xoá dấu vết soát) và KHÔNG có giá trị nào khác.
 */
export const EVIDENCE_DECIDABLE_STATUSES = [
  VERIFICATION_STATUS.VALID,
  VERIFICATION_STATUS.INVALID,
] as const;
export type EvidenceDecision = (typeof EVIDENCE_DECIDABLE_STATUSES)[number];

/** true nếu `value` là quyết định soát hợp lệ (valid|invalid). Fail-closed cho mọi giá trị lạ. */
export function isEvidenceDecision(value: unknown): value is EvidenceDecision {
  return (
    value === VERIFICATION_STATUS.VALID || value === VERIFICATION_STATUS.INVALID
  );
}

/**
 * 🔴 KHÔNG TIN MÙ (AC4/AC5): bằng chứng CHỈ được coi là căn cứ đổi trạng thái tiền khi đã có người
 * soát và kết luận `valid`. `unverified` (claim) và `invalid` ⇒ **false** ⇒ tầng gọi KHÔNG được post/
 * reverse bút toán dựa trên nó.
 *
 * Mutation đổi `=== VALID` thành `!== INVALID` (tin luôn claim) ⇒ test AC4 ĐỎ.
 */
export function isEvidenceTrustworthy(status: string | null | undefined): boolean {
  return status === VERIFICATION_STATUS.VALID;
}

/**
 * Kết luận dispute có được phép DỰA VÀO bằng chứng để đổi tiền hay không.
 *
 * Trả `true` CHỈ KHI có ÍT NHẤT MỘT bằng chứng đã soát `valid`. Tập rỗng ⇒ false. Toàn `unverified`
 * ⇒ false (claim-mù KHÔNG đủ). Toàn `invalid` ⇒ false. Fail-closed.
 */
export function canSettleFromEvidence(
  statuses: readonly (string | null | undefined)[],
): boolean {
  return statuses.some((s) => isEvidenceTrustworthy(s));
}
